/**
 * Página principal del módulo Pipelines (estilo HubSpot Deals).
 *
 * Toolbar:
 *  - Pestañas guardadas (vistas) tipo "Todos negocios", "Mis negocios" + botón "+" (guardar)
 *  - Buscar / Selector de pipeline / Filtros / Ordenar / Métricas / Exportar
 *  - Quick filters: Propietario, Fecha creación, Última actividad, Fecha cierre
 *
 * Permisos:
 *  - GET /api/pipelines/mis-permisos para saber qué puede hacer el user
 *  - Botones de crear/editar/borrar se ocultan si no tiene permiso
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import {
  Plus, Search, Settings, Briefcase, Loader2, Trash2, Edit3,
  LayoutDashboard, Filter, ArrowDownUp, BarChart3, Download, X,
  Bookmark, ShieldOff, FileText, Link2, Copy, ExternalLink, Palette,
  Ticket as TicketIcon, Upload, Eye, LayoutGrid, List as ListIcon, Clock, FileDown, CheckCircle2,
} from "lucide-react";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "../../components/ui/hover-card";
import { toast } from "sonner";
import { useAuth } from "../../App";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import PipelineAdminDialog from "./PipelineAdminDialog";
import LeadFormConfigDialog from "./LeadFormConfigDialog";
import LeadFormBrandingDialog from "./LeadFormBrandingDialog";import CardDialog from "./CardDialog";
import StageGateDialog from "./StageGateDialog";
import ImportCardsDialog from "./ImportCardsDialog";
import { getMissingStageFields } from "./fieldUtils";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../../components/ui/dropdown-menu";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => Number(n || 0).toLocaleString("es-DO", { maximumFractionDigits: 0 });

// Filtros default vacíos
const EMPTY_FILTERS = {
  search: "",
  owner_id: "",
  date_from: "", // expected close date
  date_to: "",
  created_from: "",
  created_to: "",
  last_activity_days: "",
  value_min: "",
  value_max: "",
  stage_id: "",
};
const DEFAULT_SORT = { sort_by: "updated_at", sort_dir: "desc" };

export default function PipelinesPage() {
  const { getAuthHeaders, user, token } = useAuth();
  const navigate = useNavigate();
  const isAdminGlobal = ["admin", "administrador", "superadmin"].includes((user?.role || "").toLowerCase());

  const [pipelines, setPipelines] = useState([]);
  const [activePipelineId, setActivePipelineId] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [cards, setCards] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [showCard, setShowCard] = useState(false);
  const [editingCard, setEditingCard] = useState(null);
  const [draggingCardId, setDraggingCardId] = useState(null);
  const [stageGate, setStageGate] = useState(null); // {card, stage, missing}
  const [showImport, setShowImport] = useState(false);
  const [cardInitialTab, setCardInitialTab] = useState("datos");
  // Vista Kanban/Lista + campos visibles en tarjeta (preferencia local por pipeline)
  const [viewMode, setViewMode] = useState("kanban");
  const [visibleFields, setVisibleFields] = useState({});
  // Etapas cerradas: nº de ventanas de 15 días visibles por etapa (default 1 = últimos 15 días)
  const [closedWindows, setClosedWindows] = useState({});

  // Filtros / orden / vistas guardadas
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [sort, setSort] = useState({ ...DEFAULT_SORT });
  const [views, setViews] = useState([]);
  const [activeViewId, setActiveViewId] = useState(null);
  const [showMetrics, setShowMetrics] = useState(false);

  // Permisos del user actual
  const [permsMap, setPermsMap] = useState({}); // {pipeline_id: {can_view, can_create, ...}}
  const myPerms = permsMap[activePipelineId] || {};
  const canCreate = isAdminGlobal || myPerms.can_create;
  const canEdit = isAdminGlobal || myPerms.can_edit;
  const canDelete = isAdminGlobal || myPerms.can_delete;
  const canExport = isAdminGlobal || myPerms.can_export;
  const canAdminPipeline = isAdminGlobal || myPerms.can_admin;

  // ============== FETCHERS ==============
  const fetchPermisos = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/pipelines/mis-permisos`, getAuthHeaders());
      const d = await r.json();
      setPermsMap(d || {});
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchPipelines = useCallback(async () => {
    const r = await fetch(`${API_URL}/api/pipelines`, getAuthHeaders());
    const d = await r.json();
    setPipelines(d.items || []);
    if (!activePipelineId && d.items?.length) setActivePipelineId(d.items[0].id);
  }, [getAuthHeaders, activePipelineId]);

  const fetchUsers = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/users`, getAuthHeaders());
      const d = await r.json();
      const us = Array.isArray(d) ? d : (d.users || []);
      setUsers(us.filter(u => u.is_active !== false));
    } catch { /* silencioso */ }
  }, [getAuthHeaders]);

  const fetchViews = useCallback(async () => {
    if (!activePipelineId) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/views?pipeline_id=${activePipelineId}`, getAuthHeaders());
      const d = await r.json();
      setViews(d.items || []);
    } catch { /* silencioso */ }
  }, [activePipelineId, getAuthHeaders]);

  const buildQueryParams = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) p.set(k, v);
    });
    p.set("sort_by", sort.sort_by);
    p.set("sort_dir", sort.sort_dir);
    return p.toString();
  }, [filters, sort]);

  const fetchPipelineDetail = useCallback(async () => {
    if (!activePipelineId) return;
    setLoading(true);
    try {
      const qs = buildQueryParams();
      const [pRes, cRes] = await Promise.all([
        fetch(`${API_URL}/api/pipelines/${activePipelineId}`, getAuthHeaders()).then((r) => r.json()),
        fetch(`${API_URL}/api/pipelines/${activePipelineId}/cards?${qs}`, getAuthHeaders()).then((r) => r.json()),
      ]);
      setPipeline(pRes);
      setCards(cRes.items || []);
    } catch {
      toast.error("Error al cargar pipeline");
    }
    setLoading(false);
  }, [activePipelineId, getAuthHeaders, buildQueryParams]);

  useEffect(() => { fetchPermisos(); fetchUsers(); fetchPipelines(); }, [fetchPermisos, fetchUsers, fetchPipelines]);
  useEffect(() => { fetchPipelineDetail(); fetchViews(); }, [fetchPipelineDetail, fetchViews]);

  // Reset al cambiar de pipeline
  useEffect(() => {
    setFilters({ ...EMPTY_FILTERS });
    setSort({ ...DEFAULT_SORT });
    setActiveViewId(null);
    setClosedWindows({});
    try { setVisibleFields(JSON.parse(localStorage.getItem(`pl_fields_${activePipelineId}`) || "{}")); } catch { setVisibleFields({}); }
    setViewMode(localStorage.getItem(`pl_view_${activePipelineId}`) === "lista" ? "lista" : "kanban");
  }, [activePipelineId]);

  const hideValue = pipeline?.hide_value_fields === true;
  // Vista de campos definida por el admin: aplica a todos y los no-admin no pueden cambiarla
  const adminFieldsView = pipeline?.card_fields_view && typeof pipeline.card_fields_view === "object" ? pipeline.card_fields_view : null;
  useEffect(() => {
    if (adminFieldsView) setVisibleFields(adminFieldsView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline?.card_fields_view]);
  const fieldVisible = useCallback((key, def = true) => (visibleFields[key] === undefined ? def : !!visibleFields[key]), [visibleFields]);
  const setFieldVisible = async (key, val) => {
    if (adminFieldsView && !isAdminGlobal) {
      toast.warning("La vista de campos fue definida por el administrador y no se puede modificar");
      return;
    }
    const next = { ...visibleFields, [key]: val };
    setVisibleFields(next);
    if (isAdminGlobal) {
      try {
        await fetch(`${API_URL}/api/pipelines/${activePipelineId}/card-fields-view`, {
          method: "PUT",
          headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
          body: JSON.stringify({ fields: next }),
        });
        setPipeline((p) => (p ? { ...p, card_fields_view: next } : p));
      } catch { toast.error("No se pudo guardar la vista para todos los usuarios"); }
    } else {
      try { localStorage.setItem(`pl_fields_${activePipelineId}`, JSON.stringify(next)); } catch { /* noop */ }
    }
  };
  const changeViewMode = (m) => {
    setViewMode(m);
    try { localStorage.setItem(`pl_view_${activePipelineId}`, m); } catch { /* noop */ }
  };

  // ============== KANBAN ==============
  const cardsByStage = useMemo(() => {
    const map = {};
    (pipeline?.stages || []).forEach((s) => { map[s.id] = []; });
    cards.forEach((c) => { if (map[c.stage_id]) map[c.stage_id].push(c); });
    return map;
  }, [cards, pipeline]);

  const metricsByStage = useMemo(() => {
    // En la etapa Cierre Ganado, si existe el campo personalizado "Valor Final Ganado",
    // se totaliza por ese campo (fallback al valor inicial si está vacío).
    const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
    const finalWonField = (pipeline?.fields || []).find((f) => norm(f.label) === "valor final ganado" || norm(f.name) === "valor final ganado");
    const m = {};
    (pipeline?.stages || []).forEach((s) => {
      const list = cardsByStage[s.id] || [];
      const useFinal = !!s.is_won && !!finalWonField;
      const probability = Number(s.default_probability || 0) / 100;
      // Totales agrupados por moneda de cada tarjeta (fallback: moneda del pipeline)
      const totals = {};
      list.forEach((c) => {
        let v = Number(c.value || 0);
        if (useFinal) {
          const vf = Number(c.custom_fields?.[finalWonField.name]);
          if (Number.isFinite(vf) && vf > 0) v = vf;
        }
        if (!v) return;
        const cur = c.currency || pipeline?.currency || "DOP";
        totals[cur] = (totals[cur] || 0) + v;
      });
      const total = Object.values(totals).reduce((a, b) => a + b, 0);
      m[s.id] = { count: list.length, total, totals, ponderado: total * probability, probability: s.default_probability, useFinal };
    });
    return m;
  }, [cardsByStage, pipeline]);

  // ============== DRAG & DROP ==============
  const handleDragStart = (e, cardId) => {
    if (!canEdit) { e.preventDefault(); toast.warning("No tenés permiso para mover tarjetas"); return; }
    setDraggingCardId(cardId);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const handleDrop = async (e, stageId) => {
    e.preventDefault();
    if (!draggingCardId) return;
    const card = cards.find((c) => c.id === draggingCardId);
    if (!card || card.stage_id === stageId) { setDraggingCardId(null); return; }
    const newStage = pipeline.stages.find((s) => s.id === stageId);
    // Gate de etapa: requisitos del servidor (campos de etapa + workflows: campos exigidos/comentario)
    let serverChecked = false;
    try {
      const rv = await fetch(`${API_URL}/api/pipelines/cards/${card.id}/move`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ stage_id: stageId, validate_only: true }),
      });
      if (rv.ok) {
        serverChecked = true;
        const d = await rv.json();
        if ((d.missing_fields || []).length || d.require_comment) {
          setDraggingCardId(null);
          setStageGate({
            card,
            stage: newStage,
            missing: (d.missing_fields || []).map((m) => ({
              ...m,
              core: m.name === "expected_close_date",
              field: (pipeline.fields || []).find((f) => f.name === m.name),
            })),
            requireComment: !!d.require_comment,
            commentPrompts: d.comment_prompts || [],
          });
          return;
        }
      }
    } catch { /* fallback local */ }
    if (!serverChecked) {
      const missing = getMissingStageFields(newStage, pipeline.fields, card);
      if (missing.length) {
        setDraggingCardId(null);
        setStageGate({ card, stage: newStage, missing, requireComment: false, commentPrompts: [] });
        return;
      }
    }
    let lost_reason = "";
    if (newStage?.is_lost) lost_reason = window.prompt("Motivo del cierre perdido (opcional):") || "";
    setCards((prev) => prev.map((c) => (c.id === draggingCardId ? { ...c, stage_id: stageId } : c)));
    setDraggingCardId(null);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/cards/${card.id}/move`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ stage_id: stageId, lost_reason }),
      });
      if (!r.ok) throw new Error();
      fetchPipelineDetail();
    } catch { toast.error("No se pudo mover la tarjeta"); fetchPipelineDetail(); }
  };

  const openNewCard = () => { setEditingCard(null); setCardInitialTab("datos"); setShowCard(true); };

  const downloadPlantilla = async () => {
    if (!pipeline) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipeline.id}/plantilla-import`, getAuthHeaders());
      if (!r.ok) throw new Error("No se pudo generar la plantilla");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `plantilla_${pipeline.name.replace(/\s+/g, "_")}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Plantilla descargada con los campos y etapas actualizados");
    } catch (e) { toast.error(e.message || "Error al descargar la plantilla"); }
  };
  const openEditCard = (card, tab = "datos") => { setEditingCard(card); setCardInitialTab(tab); setShowCard(true); };
  const handleDeleteCard = async (card) => {
    if (!window.confirm(`¿Eliminar la tarjeta "${card.title}"?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/cards/${card.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      if (!r.ok) throw new Error();
      toast.success("Tarjeta eliminada");
      fetchPipelineDetail();
    } catch { toast.error("Error al eliminar"); }
  };

  // ============== VISTAS (TABS) ==============
  const applyView = (view) => {
    if (!view) {
      setFilters({ ...EMPTY_FILTERS });
      setSort({ ...DEFAULT_SORT });
      setActiveViewId(null);
      return;
    }
    const f = view.filters || {};
    setFilters({ ...EMPTY_FILTERS, ...f });
    setSort({
      sort_by: f.sort_by || "updated_at",
      sort_dir: f.sort_dir || "desc",
    });
    setActiveViewId(view.id);
  };

  const handleSaveView = async () => {
    const name = window.prompt("Nombre de la vista:");
    if (!name?.trim()) return;
    try {
      const r = await fetch(`${API_URL}/api/pipelines/views`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({
          pipeline_id: activePipelineId,
          name: name.trim(),
          filters: { ...filters, sort_by: sort.sort_by, sort_dir: sort.sort_dir },
          is_shared: false,
        }),
      });
      if (!r.ok) throw new Error();
      toast.success("Vista guardada");
      fetchViews();
    } catch { toast.error("Error al guardar la vista"); }
  };

  const handleDeleteView = async (view) => {
    if (!window.confirm(`¿Eliminar la vista "${view.name}"?`)) return;
    try {
      await fetch(`${API_URL}/api/pipelines/views/${view.id}`, { method: "DELETE", headers: getAuthHeaders().headers });
      toast.success("Vista eliminada");
      if (activeViewId === view.id) applyView(null);
      fetchViews();
    } catch { toast.error("Error al eliminar la vista"); }
  };

  // ============== ACCIONES (filtros / export) ==============
  const activeFilterCount = useMemo(() =>
    Object.entries(filters).filter(([k, v]) => k !== "search" && v !== "" && v !== null).length,
  [filters]);

  const handleExport = async (formato = "xlsx") => {
    if (!canExport) { toast.warning("No tenés permiso para exportar"); return; }
    try {
      const qs = buildQueryParams();
      const r = await fetch(`${API_URL}/api/pipelines/${activePipelineId}/export?${qs}&formato=${formato}`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pipeline?.name || "pipeline"}_${new Date().toISOString().slice(0, 10)}.${formato}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(formato === "xlsx" ? "Excel completo exportado" : "CSV exportado");
    } catch { toast.error("Error al exportar"); }
  };

  const handleReporte = async () => {
    if (!canExport) { toast.warning("No tiene permiso para exportar"); return; }
    try {
      const qs = buildQueryParams();
      const r = await fetch(`${API_URL}/api/pipelines/${activePipelineId}/reporte?${qs}`, getAuthHeaders());
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_${pipeline?.name || "pipeline"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Reporte del pipeline descargado");
    } catch { toast.error("Error al generar el reporte"); }
  };

  // ============== RENDER ==============
  const hasNoAccess = !isAdminGlobal && pipelines.length === 0;

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-64 overflow-x-hidden">
        <div className="p-4 pt-14 md:pt-4 max-w-[1800px] mx-auto" data-testid="pipelines-page">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#193966] to-[#80BC00] text-white flex items-center justify-center">
                <Briefcase className="w-5 h-5" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">Pipelines</h1>
                <p className="text-xs text-slate-500">Control de procesos por etapas</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {isAdminGlobal && (
                <Button size="sm" variant="outline" onClick={() => setShowAdmin(true)} className="h-9" data-testid="pipelines-admin-btn">
                  <Settings className="w-4 h-4 mr-1.5" /> Administrar
                </Button>
              )}
              {isAdminGlobal && (
                <Button size="sm" variant="outline" onClick={() => setShowLeadForm(true)} className="h-9" data-testid="pipelines-leadform-btn">
                  <Link2 className="w-4 h-4 mr-1.5 text-[#80BC00]" /> Formulario de leads
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={() => navigate("/pipelines/paneles")} className="h-9" data-testid="pipelines-paneles-btn">
                <LayoutDashboard className="w-4 h-4 mr-1.5" /> Paneles
              </Button>
              <Button size="sm" variant="outline" onClick={() => navigate("/pipelines/informes")} className="h-9" data-testid="pipelines-informes-btn">
                <FileText className="w-4 h-4 mr-1.5" /> Informes
              </Button>
              <Button size="sm" variant="outline" onClick={downloadPlantilla} disabled={!pipeline} className="h-9" title="Descargar la plantilla Excel actualizada de este pipeline para importar masivamente" data-testid="pipelines-plantilla-btn">
                <FileDown className="w-4 h-4 mr-1.5 text-[#80BC00]" /> Plantilla
              </Button>
              {canCreate && (
                <Button size="sm" variant="outline" onClick={() => setShowImport(true)} disabled={!pipeline} className="h-9" data-testid="pipelines-import-btn">
                  <Upload className="w-4 h-4 mr-1.5 text-[#193966]" /> Importar
                </Button>
              )}
              {canCreate && (
                <Button size="sm" onClick={openNewCard} disabled={!pipeline} className="h-9 bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="pipelines-add-card-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> Agregar
                </Button>
              )}
            </div>
          </div>

          {hasNoAccess ? (
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-10 text-center bg-white">
              <ShieldOff className="w-12 h-12 text-slate-400 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-slate-700 mb-2">Sin acceso</h2>
              <p className="text-sm text-slate-600 max-w-md mx-auto">
                No tenés permisos para ver ningún pipeline. Contactá al administrador para que te habilite el acceso.
              </p>
            </div>
          ) : (
            <>
              {/* Pipeline selector + Tabs de vistas guardadas */}
              <div className="flex items-end gap-2 mb-2 border-b border-slate-200">
                <select
                  value={activePipelineId || ""}
                  onChange={(e) => setActivePipelineId(e.target.value)}
                  className="h-9 border border-slate-300 rounded-t-md rounded-b-none px-3 text-sm font-semibold bg-white min-w-[180px] -mb-px relative z-10"
                  data-testid="pipelines-selector"
                >
                  {pipelines.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <ViewTab
                  label="Todos"
                  count={pipeline?.cards_count || cards.length}
                  active={!activeViewId}
                  onClick={() => applyView(null)}
                />
                <ViewTab
                  label="Mis negocios"
                  active={filters.owner_id === user?.id && !activeViewId}
                  onClick={() => { setFilters({ ...EMPTY_FILTERS, owner_id: user?.id }); setActiveViewId(null); }}
                />
                {views.map((v) => (
                  <ViewTab
                    key={v.id}
                    label={v.name}
                    active={activeViewId === v.id}
                    onClick={() => applyView(v)}
                    onDelete={() => handleDeleteView(v)}
                  />
                ))}
                <button
                  onClick={handleSaveView}
                  className="h-8 px-2 text-slate-500 hover:text-[#193966] hover:bg-slate-100 rounded text-xs flex items-center gap-1"
                  title="Guardar vista actual"
                  data-testid="pipelines-save-view-btn"
                >
                  <Plus className="w-3.5 h-3.5" /> Vista
                </button>
              </div>

              {/* Toolbar HubSpot-style */}
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <div className="relative flex-1 min-w-[240px] max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar…"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-10 h-9"
                    data-testid="pipelines-search"
                  />
                </div>

                <FiltrosPopover filters={filters} setFilters={setFilters} users={users} pipeline={pipeline} count={activeFilterCount} />
                <OrdenarPopover sort={sort} setSort={setSort} />
                <CamposVisiblesPopover pipeline={pipeline} hideValue={hideValue} fieldVisible={fieldVisible} setFieldVisible={setFieldVisible} isAdmin={isAdminGlobal} adminEnforced={!!adminFieldsView} />
                <div className="flex items-center border border-slate-300 rounded-md overflow-hidden h-9 bg-white">
                  <button
                    onClick={() => changeViewMode("kanban")}
                    className={`h-full px-2.5 flex items-center gap-1 text-xs font-medium transition-colors ${viewMode === "kanban" ? "bg-[#193966] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid="pipelines-view-kanban-btn"
                    title="Vista Kanban"
                  >
                    <LayoutGrid className="w-4 h-4" /> Kanban
                  </button>
                  <button
                    onClick={() => changeViewMode("lista")}
                    className={`h-full px-2.5 flex items-center gap-1 text-xs font-medium transition-colors ${viewMode === "lista" ? "bg-[#193966] text-white" : "text-slate-600 hover:bg-slate-50"}`}
                    data-testid="pipelines-view-lista-btn"
                    title="Vista Lista"
                  >
                    <ListIcon className="w-4 h-4" /> Lista
                  </button>
                </div>
                <Button
                  size="sm"
                  variant={showMetrics ? "default" : "outline"}
                  onClick={() => setShowMetrics(!showMetrics)}
                  className={`h-9 ${showMetrics ? "bg-[#193966] hover:bg-[#152d52] text-white" : ""}`}
                  data-testid="pipelines-metricas-btn"
                  title={showMetrics ? "Ocultar métricas" : "Mostrar métricas"}
                >
                  <BarChart3 className="w-4 h-4 mr-1.5" /> Métricas
                </Button>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={!canExport}
                      className="h-9"
                      data-testid="pipelines-export-btn"
                      title={canExport ? "Exportar negocios" : "Sin permiso de exportación"}
                    >
                      <Download className="w-4 h-4 mr-1.5" /> Exportar
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleExport("xlsx")} data-testid="export-xlsx-option">
                      <FileText className="w-4 h-4 mr-2 text-[#5f8f00]" /> Excel completo (.xlsx) — toda la información
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleExport("csv")} data-testid="export-csv-option">
                      <Download className="w-4 h-4 mr-2 text-slate-500" /> CSV básico
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  size="sm"
                  variant="outline"
                  disabled={!canExport}
                  className="h-9 text-indigo-700 border-indigo-200"
                  onClick={handleReporte}
                  data-testid="pipelines-reporte-btn"
                  title={canExport ? "Reporte Excel con todos los campos, días por etapa y trazabilidad" : "Sin permiso de exportación"}
                >
                  <FileText className="w-4 h-4 mr-1.5" /> Reporte
                </Button>
              </div>

              {/* Quick filter chips */}
              <div className="flex items-center gap-1 mb-3 flex-wrap">
                <span className="text-xs text-slate-500 mr-1">Filtros rápidos:</span>
                <QuickChip
                  active={filters.created_from && !filters.created_to}
                  label="Creados últimos 7 días"
                  onClick={() => {
                    const d = new Date(); d.setDate(d.getDate() - 7);
                    setFilters({ ...filters, created_from: d.toISOString().slice(0, 10), created_to: "" });
                  }}
                />
                <QuickChip
                  active={filters.created_from && filters.created_to}
                  label="Creados este mes"
                  onClick={() => {
                    const d = new Date();
                    const first = new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
                    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
                    setFilters({ ...filters, created_from: first, created_to: last });
                  }}
                />
                <QuickChip
                  active={filters.last_activity_days === "7"}
                  label="Activos últimos 7d"
                  onClick={() => setFilters({ ...filters, last_activity_days: filters.last_activity_days === "7" ? "" : "7" })}
                />
                <QuickChip
                  active={filters.last_activity_days === "30"}
                  label="Activos últimos 30d"
                  onClick={() => setFilters({ ...filters, last_activity_days: filters.last_activity_days === "30" ? "" : "30" })}
                />
                <QuickChip
                  active={filters.owner_id === user?.id}
                  label="Solo míos"
                  onClick={() => setFilters({ ...filters, owner_id: filters.owner_id === user?.id ? "" : user?.id })}
                />
                {activeFilterCount > 0 && (
                  <button onClick={() => setFilters({ ...EMPTY_FILTERS })} className="text-xs text-rose-600 hover:underline ml-2" data-testid="pipelines-clear-filters-btn">
                    Limpiar filtros ({activeFilterCount})
                  </button>
                )}
              </div>

              {/* Métricas bar (HubSpot-style) — visible solo cuando showMetrics=true */}
              {showMetrics && (
                <MetricasBar pipelineId={activePipelineId} qs={buildQueryParams()} currency={pipeline?.currency} showTotals={pipeline?.show_totals !== false} />
              )}

              {/* Kanban */}
              {loading ? (
                <div className="text-center p-12"><Loader2 className="w-7 h-7 mx-auto animate-spin text-slate-400" /></div>
              ) : !pipeline ? (
                <div className="text-center p-12 text-slate-400 text-sm">Selecciona un pipeline.</div>
              ) : pipeline.stages?.length === 0 ? (
                <div className="text-center p-12 text-slate-400 text-sm">Este pipeline no tiene etapas.</div>
              ) : viewMode === "lista" ? (
                <ListaView
                  pipeline={pipeline}
                  cards={cards}
                  hideValue={hideValue}
                  fieldVisible={fieldVisible}
                  onEdit={openEditCard}
                  onDelete={handleDeleteCard}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {pipeline.stages.map((stage) => {
                    const m = metricsByStage[stage.id] || {};
                    const isClosedStage = stage.is_won || stage.is_lost;
                    const allList = cardsByStage[stage.id] || [];
                    const windows = closedWindows[stage.id] || 1;
                    let visibleList = allList;
                    let hiddenCount = 0;
                    if (isClosedStage) {
                      const limitMs = windows * 15 * 86400000;
                      const nowMs = Date.now();
                      visibleList = allList.filter((c) => {
                        const d = new Date(c.won_at || c.lost_at || c.stage_entered_at || c.updated_at || c.created_at).getTime();
                        return !d || nowMs - d <= limitMs;
                      });
                      hiddenCount = allList.length - visibleList.length;
                    }
                    return (
                      <div
                        key={stage.id}
                        className="flex-shrink-0 w-[280px] bg-slate-50 rounded-lg border border-slate-200 flex flex-col"
                        onDragOver={handleDragOver}
                        onDrop={(e) => handleDrop(e, stage.id)}
                        data-testid={`pipeline-column-${stage.id}`}
                      >
                        <div className="px-3 py-2 border-b border-slate-200 bg-[#193966]/5 rounded-t-lg" style={{ borderTopColor: stage.color, borderTopWidth: 3 }}>
                          <div className="flex items-center justify-between">
                            <span className="font-semibold text-sm text-[#193966] truncate">{stage.name}</span>
                            <Badge variant="outline" className="text-[10px] bg-[#80BC00]/10 text-[#5f8f00] border-[#80BC00]/40 font-semibold">{m.count || 0}</Badge>
                          </div>
                        </div>
                        <div className="flex-1 p-2 space-y-2 min-h-[400px] max-h-[calc(100vh-340px)] overflow-y-auto">
                          {visibleList.map((card) => {
                            const pendingWon = !!stage.is_won && (card.pending_tasks_count || 0) > 0;
                            return (
                            <Card
                              key={card.id}
                              draggable={canEdit}
                              onDragStart={(e) => handleDragStart(e, card.id)}
                              className={`hover:shadow-md hover:border-[#193966]/40 transition-all border-slate-200 border-l-4 ${pendingWon ? "border-l-amber-500 bg-amber-50/50" : "border-l-[#80BC00]/70"} ${canEdit ? "cursor-grab active:cursor-grabbing" : "cursor-default"}`}
                              data-testid={`pipeline-card-${card.id}`}
                            >
                              <CardContent className="p-3">
                                <div className="flex justify-between items-start mb-1">
                                  <button
                                    onClick={() => openEditCard(card)}
                                    className="font-semibold text-sm text-[#193966] hover:underline text-left flex-1 truncate"
                                    data-testid={`pipeline-card-title-${card.id}`}
                                  >
                                    {card.title}
                                  </button>
                                  <div className="flex gap-0.5 ml-1 opacity-60 hover:opacity-100">
                                    {canEdit && (
                                      <button onClick={() => openEditCard(card)} className="p-0.5 hover:bg-slate-100 rounded"><Edit3 className="w-3 h-3 text-slate-500" /></button>
                                    )}
                                    {canDelete && (
                                      <button onClick={() => handleDeleteCard(card)} className="p-0.5 hover:bg-rose-50 rounded"><Trash2 className="w-3 h-3 text-rose-500" /></button>
                                    )}
                                  </div>
                                </div>
                                {card.value > 0 && !hideValue && fieldVisible("value") && (
                                  <p className="text-xs text-slate-600 mb-1">Valor: <span className="font-mono font-semibold text-[#5f8f00]">{fmt(card.value)} {card.currency || pipeline.currency}</span></p>
                                )}
                                {card.expected_close_date && !hideValue && fieldVisible("expected_close_date") && (
                                  <p className="text-[11px] text-slate-500">Cierre: {new Date(card.expected_close_date).toLocaleDateString("es-DO")}</p>
                                )}
                                {(pipeline.fields || []).filter((f) => fieldVisible(`cf_${f.name}`, false) && card.custom_fields?.[f.name]).map((f) => (
                                  <p key={f.name} className="text-[11px] text-slate-500 truncate" data-testid={`card-cfv-${card.id}-${f.name}`}>
                                    <span className="text-slate-400">{f.label}:</span>{" "}
                                    {Array.isArray(card.custom_fields[f.name]) ? card.custom_fields[f.name].join(", ") : String(card.custom_fields[f.name])}
                                  </p>
                                ))}
                                {card.concluido && (
                                  <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`card-concluido-${card.id}`}>
                                    <CheckCircle2 className="w-3 h-3" /> CONCLUIDO
                                  </span>
                                )}
                                {card.tasks_badge?.total > 0 && (
                                  <div className="mt-1.5" data-testid={`card-tareas-badge-${card.id}`}>
                                    <HoverCard openDelay={150} closeDelay={120}>
                                      <HoverCardTrigger asChild>
                                        <button
                                          type="button"
                                          onClick={() => openEditCard(card, "tareas")}
                                      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border cursor-pointer ${
                                        card.tasks_badge.pendientes > 0
                                          ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200"
                                          : "bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100"
                                      }`}
                                    >
                                      <Clock className="w-3 h-3" />
                                      {card.tasks_badge.pendientes > 0
                                        ? `${card.tasks_badge.pendientes} tarea${card.tasks_badge.pendientes === 1 ? "" : "s"} pendiente${card.tasks_badge.pendientes === 1 ? "" : "s"}`
                                        : `${card.tasks_badge.total} tarea${card.tasks_badge.total === 1 ? "" : "s"} realizada${card.tasks_badge.total === 1 ? "" : "s"}`}
                                    </button>
                                      </HoverCardTrigger>
                                      {/* Hover: detalle de tareas con estado y SLA (portal: no se recorta en la columna) */}
                                      <HoverCardContent align="start" side="bottom" className="w-80 p-2 max-h-64 overflow-y-auto z-[70]" data-testid={`card-tareas-popover-${card.id}`}>
                                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500 mb-1">Tareas de este negocio</p>
                                        {(card.tasks_badge.tasks || []).map((tk) => (
                                          <div key={tk.id} className="flex items-start justify-between gap-2 py-1 border-b border-slate-100 last:border-0">
                                            <div className="min-w-0">
                                              <p className={`text-[11px] font-medium truncate ${tk.done ? "line-through text-slate-400" : "text-slate-800"}`}>{tk.title}</p>
                                              <p className="text-[10px] text-slate-400 truncate">
                                                {tk.task_type_name ? `${tk.task_type_name} · ` : ""}{tk.due_date ? `vence ${tk.due_date}` : "sin fecha"}{tk.helpdesk_ticket_numero ? ` · Ticket #${tk.helpdesk_ticket_numero}` : ""}
                                              </p>
                                            </div>
                                            <div className="flex flex-col items-end gap-0.5 shrink-0">
                                              <span className={`text-[9px] font-bold px-1 py-0.5 rounded ${tk.done ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                                                {tk.done ? "Realizada" : "Pendiente"}
                                              </span>
                                              {{ en_tiempo: ["En tiempo", "bg-green-100 text-green-700"], por_vencer: ["Por vencer", "bg-amber-100 text-amber-700"], vencida: ["Vencida", "bg-red-100 text-red-700"], cumplida: ["En SLA", "bg-emerald-100 text-emerald-700"], incumplida: ["Fuera SLA", "bg-rose-100 text-rose-700"] }[tk.sla_status] && (
                                                <span className={`text-[9px] font-semibold px-1 py-0.5 rounded ${{ en_tiempo: "bg-green-100 text-green-700", por_vencer: "bg-amber-100 text-amber-700", vencida: "bg-red-100 text-red-700", cumplida: "bg-emerald-100 text-emerald-700", incumplida: "bg-rose-100 text-rose-700" }[tk.sla_status]}`}>
                                                  {{ en_tiempo: "En tiempo", por_vencer: "Por vencer", vencida: "Vencida", cumplida: "En SLA", incumplida: "Fuera SLA" }[tk.sla_status]}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                        {card.tasks_badge.total > (card.tasks_badge.tasks || []).length && (
                                          <p className="text-[10px] text-slate-400 mt-1">… y {card.tasks_badge.total - card.tasks_badge.tasks.length} más</p>
                                        )}
                                      </HoverCardContent>
                                    </HoverCard>
                                  </div>
                                )}
                                {card.owner_name && fieldVisible("owner") && (
                                  <div className="mt-2 flex items-center gap-1.5">
                                    <div className="w-5 h-5 rounded-full bg-[#193966] text-white text-[9px] font-bold flex items-center justify-center">
                                      {(card.owner_name || "?").split(" ").slice(0, 2).map((w) => w[0]).join("")}
                                    </div>
                                    <span className="text-[11px] text-slate-600 truncate">{card.owner_name}</span>
                                  </div>
                                )}
                                {Array.isArray(card.helpdesk_tickets) && card.helpdesk_tickets.length > 0 && (
                                  <div className="mt-2 flex flex-wrap gap-1" data-testid={`card-tickets-${card.id}`}>
                                    {card.helpdesk_tickets.slice(0, 3).map((tk) => (
                                      <span
                                        key={tk.ticket_id}
                                        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-sky-300 bg-sky-50 text-sky-700"
                                        title={`Ticket en Mesa de Ayuda #${tk.numero}`}
                                      >
                                        <TicketIcon className="w-3 h-3" /> #{tk.numero}
                                      </span>
                                    ))}
                                    {card.helpdesk_tickets.length > 3 && (
                                      <span className="text-[10px] text-slate-400">+{card.helpdesk_tickets.length - 3}</span>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                            );
                          })}
                          {isClosedStage && hiddenCount > 0 && (
                            <button
                              onClick={() => setClosedWindows((p) => ({ ...p, [stage.id]: windows + 1 }))}
                              className="w-full text-xs text-[#193966] font-semibold border border-dashed border-[#193966]/30 rounded py-1.5 hover:bg-[#193966]/5 transition-colors"
                              data-testid={`stage-ver-mas-${stage.id}`}
                            >
                              Ver más ({windows * 15 + 1}–{(windows + 1) * 15} días) · {hiddenCount} oculto{hiddenCount === 1 ? "" : "s"}
                            </button>
                          )}
                          {isClosedStage && allList.length > 0 && (
                            <p className="text-center text-[10px] text-slate-400" data-testid={`stage-visible-info-${stage.id}`}>
                              Mostrando cerrados de los últimos {windows * 15} días ({visibleList.length} de {allList.length})
                            </p>
                          )}
                        </div>
                        {pipeline.show_totals !== false && (
                        <div className="px-3 py-2 border-t border-slate-200 bg-white" data-testid={`stage-totals-${stage.id}`}>
                          {Object.keys(m.totals || {}).length === 0 ? (
                            <p className="text-[11px] font-mono text-slate-700"><strong>0</strong> {pipeline.currency} <span className="text-slate-400">| {m.useFinal ? "total (Valor Final Ganado)" : "total"}</span></p>
                          ) : (
                            Object.entries(m.totals).map(([cur, t]) => (
                              <p key={cur} className="text-[11px] font-mono text-slate-700"><strong>{fmt(t)}</strong> {cur} <span className="text-slate-400">| {m.useFinal ? "total (Valor Final Ganado)" : "total"}</span></p>
                            ))
                          )}
                          {Object.entries(m.totals || {}).map(([cur, t]) => (
                            <p key={`p-${cur}`} className="text-[10px] font-mono text-slate-500">{fmt(t * (Number(m.probability || 0) / 100))} {cur} ({m.probability}%) <span className="text-slate-400">| ponderado</span></p>
                          ))}
                        </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {showAdmin && pipeline && (
            <PipelineAdminDialog
              pipelines={pipelines}
              activePipeline={pipeline}
              onClose={() => setShowAdmin(false)}
              onChanged={() => { fetchPipelines(); fetchPipelineDetail(); }}
            />
          )}
          {showCard && pipeline && (
            <CardDialog
              pipeline={pipeline}
              editing={editingCard}
              initialTab={cardInitialTab}
              onClose={() => { setShowCard(false); setEditingCard(null); setCardInitialTab("datos"); }}
              onSaved={() => { fetchPipelineDetail(); setShowCard(false); setEditingCard(null); setCardInitialTab("datos"); }}
              onSavedKeepOpen={() => fetchPipelineDetail()}
            />
          )}
          {showImport && pipeline && (
            <ImportCardsDialog
              pipeline={pipeline}
              onClose={() => setShowImport(false)}
              onImported={() => fetchPipelineDetail()}
            />
          )}
          {stageGate && (
            <StageGateDialog
              card={stageGate.card}
              stage={stageGate.stage}
              missing={stageGate.missing}
              requireComment={stageGate.requireComment}
              commentPrompts={stageGate.commentPrompts}
              onClose={() => setStageGate(null)}
              onDone={() => { setStageGate(null); fetchPipelineDetail(); }}
            />
          )}
          {showLeadForm && (
            <LeadFormShareDialog salesPipelineId={(pipelines.find((p) => p.is_sales_preset) || pipelines.find((p) => p.name === "Negocios") || pipelines[0])?.id} getAuthHeaders={getAuthHeaders} token={token} onClose={() => setShowLeadForm(false)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// SUBCOMPONENTES
// ============================================================
function ViewTab({ label, count, active, onClick, onDelete }) {
  return (
    <div className={`group flex items-center gap-1 px-3 h-8 rounded-t-md text-sm cursor-pointer transition-colors -mb-px ${active ? "bg-white border border-b-white border-slate-200 text-[#193966] font-semibold" : "text-slate-600 hover:bg-slate-100"}`}>
      <button onClick={onClick} className="flex items-center gap-1.5" data-testid={`pipelines-view-tab-${label}`}>
        {active && <Bookmark className="w-3 h-3 fill-current" />}
        {label}
        {typeof count === "number" && (
          <span className="ml-1 text-[10px] bg-slate-200 text-slate-700 rounded-full px-1.5 py-0.5">{fmt(count)}</span>
        )}
      </button>
      {onDelete && (
        <button onClick={(e) => { e.stopPropagation(); onDelete(); }} className="opacity-0 group-hover:opacity-100 text-rose-500 hover:text-rose-700">
          <X className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// Popover para elegir qué campos ver en la tarjeta/lista (preferencia local o vista global del admin)
function CamposVisiblesPopover({ pipeline, hideValue, fieldVisible, setFieldVisible, isAdmin, adminEnforced }) {
  const locked = adminEnforced && !isAdmin;
  const core = [
    ...(!hideValue ? [{ key: "value", label: "Valor" }, { key: "expected_close_date", label: "Fecha de cierre" }] : []),
    { key: "owner", label: "Propietario" },
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-9" data-testid="pipelines-campos-btn" title="Elegir campos visibles en la tarjeta">
          <Eye className="w-4 h-4 mr-1.5" /> Campos
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px]" align="end">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Campos visibles en la tarjeta</p>
        <div className="space-y-1.5 max-h-[320px] overflow-y-auto">
          {core.map((c) => (
            <label key={c.key} className={`flex items-center gap-2 text-sm text-slate-700 ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
              <input type="checkbox" checked={fieldVisible(c.key)} disabled={locked} onChange={(e) => setFieldVisible(c.key, e.target.checked)} className="w-4 h-4 accent-[#193966]" data-testid={`campo-visible-${c.key}`} />
              {c.label}
            </label>
          ))}
          {(pipeline?.fields || []).length > 0 && <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide pt-1.5">Campos del pipeline</p>}
          {(pipeline?.fields || []).map((f) => (
            <label key={f.id} className={`flex items-center gap-2 text-sm text-slate-700 ${locked ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}>
              <input type="checkbox" checked={fieldVisible(`cf_${f.name}`, false)} disabled={locked} onChange={(e) => setFieldVisible(`cf_${f.name}`, e.target.checked)} className="w-4 h-4 accent-[#193966]" data-testid={`campo-visible-cf-${f.name}`} />
              {f.label}
            </label>
          ))}
        </div>
        {isAdmin ? (
          <p className="text-[10px] text-indigo-500 mt-2 font-medium" data-testid="campos-nota-admin">Como administrador, esta selección se aplica a todos los usuarios de este pipeline.</p>
        ) : locked ? (
          <p className="text-[10px] text-amber-600 mt-2 font-medium" data-testid="campos-nota-bloqueado">Vista definida por el administrador. No se puede modificar.</p>
        ) : (
          <p className="text-[10px] text-slate-400 mt-2">Esta preferencia se guarda en este navegador, por pipeline.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Vista de Lista (tabla) del pipeline
function ListaView({ pipeline, cards, hideValue, fieldVisible, onEdit, onDelete, canEdit, canDelete }) {
  const stagesById = Object.fromEntries((pipeline.stages || []).map((s) => [s.id, s]));
  const customCols = (pipeline.fields || []).filter((f) => fieldVisible(`cf_${f.name}`, false));
  const showValue = !hideValue && fieldVisible("value");
  const showClose = !hideValue && fieldVisible("expected_close_date");
  const showOwner = fieldVisible("owner");
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto" data-testid="pipelines-lista-view">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b">
          <tr>
            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Título</th>
            <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Etapa</th>
            {showOwner && <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Propietario</th>}
            {showValue && <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Valor</th>}
            {showClose && <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Cierre</th>}
            {customCols.map((f) => <th key={f.id} className="text-left px-3 py-2 text-xs font-semibold text-slate-600">{f.label}</th>)}
            <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Tareas pend.</th>
            <th className="text-center px-3 py-2 text-xs font-semibold text-slate-600">Tickets</th>
            <th className="px-2 py-2" />
          </tr>
        </thead>
        <tbody>
          {cards.map((card) => {
            const stage = stagesById[card.stage_id];
            const pendingWon = !!stage?.is_won && (card.pending_tasks_count || 0) > 0;
            return (
              <tr key={card.id} className={`border-b hover:bg-slate-50 ${pendingWon ? "bg-amber-50/60" : ""}`} data-testid={`lista-row-${card.id}`}>
                <td className="px-3 py-2">
                  <button onClick={() => onEdit(card)} className="font-semibold text-[#193966] hover:underline text-left" data-testid={`lista-title-${card.id}`}>{card.title}</button>
                </td>
                <td className="px-3 py-2">
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: stage?.color || "#64748B" }} />
                    {stage?.name || "—"}
                  </span>
                  {card.concluido && (
                    <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-bold px-1 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`lista-concluido-${card.id}`}>
                      <CheckCircle2 className="w-2.5 h-2.5" /> CONCLUIDO
                    </span>
                  )}
                </td>
                {showOwner && <td className="px-3 py-2 text-xs text-slate-600">{card.owner_name || "—"}</td>}
                {showValue && <td className="px-3 py-2 text-xs font-mono text-right text-[#5f8f00]">{card.value > 0 ? `${fmt(card.value)} ${card.currency || pipeline.currency}` : "—"}</td>}
                {showClose && <td className="px-3 py-2 text-xs text-slate-600">{card.expected_close_date ? new Date(card.expected_close_date).toLocaleDateString("es-DO") : "—"}</td>}
                {customCols.map((f) => {
                  const v = card.custom_fields?.[f.name];
                  return <td key={f.id} className="px-3 py-2 text-xs text-slate-600 max-w-[180px] truncate">{v ? (Array.isArray(v) ? v.join(", ") : String(v)) : "—"}</td>;
                })}
                <td className="px-3 py-2 text-center">
                  {(card.pending_tasks_count || 0) > 0 ? (
                    <button
                      type="button"
                      onClick={() => onEdit(card, "tareas")}
                      title="Ver las tareas pendientes de esta tarjeta"
                      className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border cursor-pointer hover:underline ${pendingWon ? "bg-amber-100 text-amber-800 border-amber-300 hover:bg-amber-200" : "bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-200"}`}
                      data-testid={`lista-pending-tasks-${card.id}`}
                    >
                      <Clock className="w-3 h-3" /> {card.pending_tasks_count}
                    </button>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </td>
                <td className="px-3 py-2 text-center">
                  {Array.isArray(card.helpdesk_tickets) && card.helpdesk_tickets.length > 0 ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded border border-sky-300 bg-sky-50 text-sky-700">
                      <TicketIcon className="w-3 h-3" /> {card.helpdesk_tickets.length}
                    </span>
                  ) : <span className="text-xs text-slate-300">—</span>}
                </td>
                <td className="px-2 py-2">
                  <div className="flex gap-1 justify-end">
                    {canEdit && <button onClick={() => onEdit(card)} className="p-1 hover:bg-slate-100 rounded"><Edit3 className="w-3.5 h-3.5 text-slate-500" /></button>}
                    {canDelete && <button onClick={() => onDelete(card)} className="p-1 hover:bg-rose-50 rounded"><Trash2 className="w-3.5 h-3.5 text-rose-500" /></button>}
                  </div>
                </td>
              </tr>
            );
          })}
          {cards.length === 0 && (
            <tr><td colSpan={20} className="text-center py-8 text-xs text-slate-400">No hay negocios con los filtros actuales.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function QuickChip({ label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`h-7 px-2.5 text-xs rounded-full border transition-colors ${active ? "bg-[#193966] text-white border-[#193966]" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
      data-testid={`pipelines-quickchip-${label.replace(/\s+/g, "-").toLowerCase()}`}
    >
      {label}
    </button>
  );
}

function FiltrosPopover({ filters, setFilters, users, pipeline, count }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-9" data-testid="pipelines-filtros-btn">
          <Filter className="w-4 h-4 mr-1.5" /> Filtros
          {count > 0 && <Badge className="ml-1.5 bg-[#80BC00] text-white text-[10px] h-4 px-1.5">{count}</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[360px]" align="end">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Filtros avanzados</p>
        <div className="space-y-2.5">
          <div>
            <label className="text-[11px] text-slate-600">Propietario del negocio</label>
            <select value={filters.owner_id} onChange={(e) => setFilters({ ...filters, owner_id: e.target.value })} className="w-full h-8 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="filtro-owner">
              <option value="">Todos</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[11px] text-slate-600">Etapa</label>
            <select value={filters.stage_id} onChange={(e) => setFilters({ ...filters, stage_id: e.target.value })} className="w-full h-8 border border-slate-300 rounded px-2 text-sm bg-white" data-testid="filtro-stage">
              <option value="">Todas</option>
              {(pipeline?.stages || []).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-slate-600">Cierre desde</label>
              <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="h-8" data-testid="filtro-cierre-from" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Cierre hasta</label>
              <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="h-8" data-testid="filtro-cierre-to" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-slate-600">Creación desde</label>
              <Input type="date" value={filters.created_from?.slice(0, 10) || ""} onChange={(e) => setFilters({ ...filters, created_from: e.target.value })} className="h-8" data-testid="filtro-creacion-from" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Creación hasta</label>
              <Input type="date" value={filters.created_to?.slice(0, 10) || ""} onChange={(e) => setFilters({ ...filters, created_to: e.target.value })} className="h-8" data-testid="filtro-creacion-to" />
            </div>
          </div>
          <div>
            <label className="text-[11px] text-slate-600">Última actividad (últimos N días)</label>
            <Input type="number" min="1" value={filters.last_activity_days} onChange={(e) => setFilters({ ...filters, last_activity_days: e.target.value })} className="h-8" placeholder="ej. 30" data-testid="filtro-actividad" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[11px] text-slate-600">Valor mínimo</label>
              <Input type="number" value={filters.value_min} onChange={(e) => setFilters({ ...filters, value_min: e.target.value })} className="h-8" data-testid="filtro-valor-min" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Valor máximo</label>
              <Input type="number" value={filters.value_max} onChange={(e) => setFilters({ ...filters, value_max: e.target.value })} className="h-8" data-testid="filtro-valor-max" />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function OrdenarPopover({ sort, setSort }) {
  const fields = [
    { value: "updated_at", label: "Última actividad" },
    { value: "created_at", label: "Fecha de creación" },
    { value: "expected_close_date", label: "Fecha de cierre" },
    { value: "value", label: "Valor" },
    { value: "title", label: "Título (A-Z)" },
  ];
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className="h-9" data-testid="pipelines-ordenar-btn">
          <ArrowDownUp className="w-4 h-4 mr-1.5" /> Ordenar
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px]" align="end">
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Ordenar por</p>
        <div className="space-y-1">
          {fields.map(f => (
            <button
              key={f.value}
              onClick={() => setSort({ ...sort, sort_by: f.value })}
              className={`w-full text-left text-sm px-2 py-1.5 rounded hover:bg-slate-100 ${sort.sort_by === f.value ? "bg-slate-100 text-[#193966] font-semibold" : "text-slate-700"}`}
              data-testid={`ordenar-${f.value}`}
            >{f.label}</button>
          ))}
        </div>
        <div className="border-t mt-2 pt-2 flex gap-2">
          <button onClick={() => setSort({ ...sort, sort_dir: "desc" })} className={`flex-1 text-xs py-1 rounded ${sort.sort_dir === "desc" ? "bg-[#193966] text-white" : "bg-slate-100"}`}>Desc</button>
          <button onClick={() => setSort({ ...sort, sort_dir: "asc" })} className={`flex-1 text-xs py-1 rounded ${sort.sort_dir === "asc" ? "bg-[#193966] text-white" : "bg-slate-100"}`}>Asc</button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function MetricasBar({ pipelineId, qs, currency, showTotals = true }) {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!pipelineId) return;
    fetch(`${API_URL}/api/pipelines/${pipelineId}/metricas?${qs}`, getAuthHeaders())
      .then(r => r.json()).then(setData).catch(() => setData(null));
  }, [pipelineId, qs, getAuthHeaders]);

  if (!data) {
    return (
      <div className="mb-3 grid grid-cols-6 gap-2">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-[90px] bg-slate-100 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  const c = currency || "";
  const cards = [
    { label: "Cantidad total de negocios", value: fmt(data.total), color: "#193966" },
    { label: "Valor total ponderado", value: `${fmt(data.avg_value)} ${c}`, color: "#3B82F6", money: true },
    { label: "Negocios abiertos", value: fmt((data.total || 0) - (data.by_stage || []).filter(s => s.is_won || s.is_lost).reduce((a, s) => a + (s.count || 0), 0)), color: "#0EA5E9" },
    { label: "Valor de negocios ganados", value: `${fmt(data.won_value)} ${c}`, color: "#10B981", money: true },
    { label: "Won rate", value: `${data.won_rate}%`, color: "#80BC00" },
    { label: "Negocios atrasados", value: fmt(data.overdue), color: "#EF4444" },
  ].filter(m => showTotals || !m.money);

  const gridCols = { 4: "grid-cols-4", 5: "grid-cols-5", 6: "grid-cols-6" }[cards.length] || "grid-cols-6";

  return (
    <div className={`mb-3 grid ${gridCols} gap-2`} data-testid="pipelines-metrics-bar">
      {cards.map((m, i) => (
        <div
          key={i}
          className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm hover:shadow-md transition-shadow"
          data-testid={`pipelines-metric-card-${i}`}
        >
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-500 leading-tight">{m.label}</p>
          <p className="text-2xl font-bold mt-1 tabular-nums truncate" style={{ color: m.color }} title={m.value}>{m.value}</p>
        </div>
      ))}
    </div>
  );
}

function KpiCell({ label, value, color }) {
  return (
    <div className="bg-slate-50 rounded p-2">
      <p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}

// ============================================================
// LeadFormShareDialog — comparte el link/QR del formulario público de leads
// ============================================================
function LeadFormShareDialog({ salesPipelineId, getAuthHeaders, token, onClose }) {
  const publicUrl = `${window.location.origin}/captar-lead`;
  const qrUrl = `${API_URL}/api/pipelines/public/lead-form/qr?url=${encodeURIComponent(publicUrl)}`;
  const [showConfig, setShowConfig] = useState(false);
  const [showBranding, setShowBranding] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado al portapapeles");
    } catch {
      toast.error("No se pudo copiar el link");
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="leadform-share-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#80BC00]" /> Formulario de captación de leads
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-slate-600">
          Compartí este enlace en tu web, redes o WhatsApp. Cada solicitud crea automáticamente una
          oportunidad <b>sin asignar</b> en el pipeline comercial, con origen <b>Web</b>.
        </p>

        <div className="flex items-center justify-center py-3">
          <img src={qrUrl} alt="Código QR del formulario" className="w-44 h-44 rounded-lg border border-slate-200" data-testid="leadform-qr" />
        </div>

        <div className="flex items-center gap-2">
          <Input value={publicUrl} readOnly className="h-9 text-sm font-mono" data-testid="leadform-url" />
          <Button size="sm" variant="outline" onClick={copy} className="h-9 shrink-0" data-testid="leadform-copy">
            <Copy className="w-4 h-4" />
          </Button>
        </div>

        {salesPipelineId && (
          <div className="grid grid-cols-2 gap-2 mt-1">
            <Button variant="outline" onClick={() => setShowConfig(true)} data-testid="leadform-config-btn">
              <Settings className="w-4 h-4 mr-1.5" /> Configurar campos
            </Button>
            <Button variant="outline" onClick={() => setShowBranding(true)} data-testid="leadform-branding-btn">
              <Palette className="w-4 h-4 mr-1.5" /> Personalizar apariencia
            </Button>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="leadform-close">Cerrar</Button>
          <a href={publicUrl} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#193966] hover:bg-[#142d4f] text-white" data-testid="leadform-open">
              <ExternalLink className="w-4 h-4 mr-1.5" /> Abrir formulario
            </Button>
          </a>
        </DialogFooter>
      </DialogContent>

      {showConfig && (
        <LeadFormConfigDialog pipelineId={salesPipelineId} getAuthHeaders={getAuthHeaders} onClose={() => setShowConfig(false)} />
      )}
      {showBranding && (
        <LeadFormBrandingDialog pipelineId={salesPipelineId} getAuthHeaders={getAuthHeaders} token={token} onClose={() => setShowBranding(false)} />
      )}
    </Dialog>
  );
}
